import React from 'react';
import { authService } from '../api/authService';

/**
 * Custom hook to check if the current user has a specific permission.
 * 
 * @param {string|string[]} permissions - The permission slug(s) to check.
 * If an array is provided, it checks if the user has ANY of the permissions.
 * @returns {boolean} True if user has permission or is admin, false otherwise.
 */
export function usePermission(permissions) {
  if (!permissions) return true;
  
  if (Array.isArray(permissions)) {
    return permissions.some(perm => authService.hasPermission(perm));
  }
  
  return authService.hasPermission(permissions);
}

/**
 * Component wrapper that only renders its children if the current user
 * has the required permission(s).
 * 
 * @param {Object} props
 * @param {string|string[]} props.permission - The permission slug(s) required.
 * @param {React.ReactNode} props.children - The content to render.
 * @param {React.ReactNode} [props.fallback=null] - Optional fallback to render if unauthorized.
 */
export function HasPermission({ permission, children, fallback = null }) {
  const hasAccess = usePermission(permission);
  
  if (!hasAccess) {
    return fallback;
  }
  
  return <>{children}</>;
}
